// src/pages/analytics/OpsAuditTab.tsx
//
// Operations Audit — three exception finders behind a segmented control:
//   1. Shipped Not Invoiced (recipe: ops-shipped-not-invoiced) — orders that
//      went out the door but never became invoices.
//   2. Voided Orders (recipe: ops-void-orders) — voided in a date range; the
//      audit catch is a void AFTER shipping (shipped column highlighted red).
//   3. Short Shipped (recipe: ops-short-shipped) — vendor/item lines where
//      shipped < ordered, with short % severity coloring.
//
// Date strings from the agent are rendered as-is, never through new Date().

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert, RefreshCw, Download, FileSpreadsheet, RotateCcw, AlertTriangle, Check } from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import DateField from '../../components/DateField';
import type { OpsSniRow, OpsVoidRow, OpsShortRow, OpsShortResponse, OpsShortMonth } from '../../types';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import { FilterSelect } from './salesFilterBar';
import { subcategoryOptions } from './itemSubcategories';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(Math.round(v));

const isoDaysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

type View = 'sni' | 'void' | 'short';

const VIEWS: { key: View; label: string }[] = [
  { key: 'sni',   label: 'Shipped Not Invoiced' },
  { key: 'void',  label: 'Voided Orders' },
  { key: 'short', label: 'Short Shipped' },
];

function ErrorBox({ title, error }: { title: string; error: unknown }) {
  return (
    <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
      <AlertTriangle size={16} />
      <div>
        <div className="font-medium">{title}</div>
        <div className="text-xs text-red-300/70 mt-0.5">{(error as Error).message}</div>
      </div>
    </div>
  );
}

function Spinner() {
  return <div className="flex items-center justify-center h-32"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>;
}

function DateRange({ from, to, onFrom, onTo }: { from: string; to: string; onFrom: (v: string) => void; onTo: (v: string) => void }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Date Range</span>
      <div className="flex items-center gap-1.5">
        <DateField value={from} onChange={onFrom} className="w-[130px]" />
        <span className="text-slate-600 text-xs">→</span>
        <DateField value={to} onChange={onTo} className="w-[130px]" />
      </div>
    </div>
  );
}

function ExportButton({ onClick, disabled }: { onClick: () => void; disabled: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled} title="Export to CSV"
      className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
      <Download size={11} /> CSV
    </button>
  );
}

function ExcelButton({ onClick, disabled }: { onClick: () => void; disabled: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled} title="Export to Excel"
      className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
      <FileSpreadsheet size={11} /> Excel
    </button>
  );
}

function ResetButton({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} title="Reset all filters to defaults"
      className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
      <RotateCcw size={11} /> Reset
    </button>
  );
}

function RefreshButton({ onClick, loading, fetching }: { onClick: () => void; loading: boolean; fetching: boolean }) {
  return (
    <button onClick={onClick} disabled={loading}
      className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
      <RefreshCw size={14} className={fetching ? 'animate-spin' : ''} />
    </button>
  );
}

export function OpsAuditTab() {
  const [view, setView] = useState<View>('sni');

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <ShieldAlert size={20} /> Operations Audit
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Exception finders — shipped-not-invoiced orders, voided orders, and vendor short-shipments.
        </p>
      </div>

      {/* Segmented control */}
      <div className="inline-flex rounded-lg border border-slate-700/50 bg-slate-900/40 p-1 gap-1 max-w-full overflow-x-auto">
        {VIEWS.map(v => (
          <button key={v.key} onClick={() => setView(v.key)}
            className={`px-3 py-1.5 text-xs rounded-md whitespace-nowrap transition-all ${
              view === v.key
                ? 'bg-blue-500/15 text-blue-200 border border-blue-500/30'
                : 'text-slate-400 hover:text-slate-200 border border-transparent'
            }`}>
            {v.label}
          </button>
        ))}
      </div>

      {view === 'sni'   && <SniView />}
      {view === 'void'  && <VoidView />}
      {view === 'short' && <ShortView />}
    </div>
  );
}

// ── Shipped Not Invoiced ─────────────────────────────────────────

function exportSni(rows: OpsSniRow[], exporter: typeof exportCsv = exportCsv) {
  const cols: CsvColumns<OpsSniRow> = [
    ['Order #',    (r) => r.orderNo],
    ['Code',       (r) => r.code],
    ['Customer',   (r) => r.name],
    ['Order Date', (r) => r.orderDate],
    ['Req Date',   (r) => r.reqDate],
    ['Ship Date',  (r) => r.shipDate ?? ''],
    ['Picked',     (r) => (r.picked ? 'Y' : 'N')],
    ['Total',      (r) => r.total],
    ['Balance',    (r) => r.balance],
  ];
  exporter('ops-shipped-not-invoiced', cols, rows);
}

function SniView() {
  const q = useQuery({
    queryKey: ['opsAudit', 'sni'],
    queryFn: () => api.getOpsShippedNotInvoiced(),
    staleTime: 60_000,
    retry: 1,
  });
  const rows = q.data?.rows ?? [];
  const totals = q.data?.totals;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <p className="text-xs text-slate-500">
          Orders shipped or picked but never invoiced{q.data && <> · as of <span className="text-slate-400">{q.data.asOf}</span></>}
        </p>
        <div className="flex-1" />
        <ExportButton onClick={() => exportSni(rows)} disabled={rows.length === 0} />
        <ExcelButton onClick={() => exportSni(rows, exportXlsx)} disabled={rows.length === 0} />
        <RefreshButton onClick={() => q.refetch()} loading={q.isLoading} fetching={q.isFetching} />
      </div>

      {q.error && <ErrorBox title="Couldn't load shipped-not-invoiced orders" error={q.error} />}
      {q.isLoading ? <Spinner /> : !q.error && (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/50">
                  <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium">Order #</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Customer</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Order Date</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Req Date</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Ship Date</th>
                  <th className="px-3 py-2.5 text-center text-slate-400 font-medium">Picked</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Total</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.orderNo}-${i}`} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                    <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 font-mono text-slate-300">{r.orderNo}</td>
                    <td className="px-3 py-2 text-white whitespace-nowrap max-w-[240px] truncate" title={`${r.code} ${r.name}`}>{r.name}</td>
                    <td className="px-3 py-2 text-slate-400 font-mono whitespace-nowrap">{r.orderDate}</td>
                    <td className="px-3 py-2 text-slate-400 font-mono whitespace-nowrap">{r.reqDate}</td>
                    <td className="px-3 py-2 text-slate-400 font-mono whitespace-nowrap">{r.shipDate ?? '—'}</td>
                    <td className="px-3 py-2 text-center">{r.picked ? <Check size={13} className="inline text-emerald-400" /> : <span className="text-slate-600">—</span>}</td>
                    <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtCur(r.total)}</td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtCur(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
              {totals && rows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-600/60 bg-slate-800/60 font-semibold">
                    <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 text-white" colSpan={1}>Total</td>
                    <td className="px-3 py-2 text-slate-400" colSpan={5}>{fmtNum(totals.rows)} order{totals.rows === 1 ? '' : 's'}</td>
                    <td className="px-3 py-2 text-right text-white tabular-nums">{fmtCur(totals.total)}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {rows.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">Nothing shipped-not-invoiced — all clear</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Voided Orders ────────────────────────────────────────────────

function exportVoids(rows: OpsVoidRow[], exporter: typeof exportCsv = exportCsv) {
  const cols: CsvColumns<OpsVoidRow> = [
    ['Order #',    (r) => r.orderNo],
    ['Code',       (r) => r.code],
    ['Customer',   (r) => r.name],
    ['Order Date', (r) => r.orderDate],
    ['Req Date',   (r) => r.reqDate],
    ['Shipped',    (r) => (r.shipped ? 'Y' : 'N')],
    ['Total',      (r) => r.total],
  ];
  exporter('ops-void-orders', cols, rows);
}

function VoidView() {
  const [dateFrom, setDateFrom] = useState(() => isoDaysAgo(90));
  const [dateTo, setDateTo] = useState(() => isoDaysAgo(0));
  const resetFilters = () => { setDateFrom(isoDaysAgo(90)); setDateTo(isoDaysAgo(0)); };

  const q = useQuery({
    queryKey: ['opsAudit', 'void', dateFrom, dateTo],
    queryFn: () => api.getOpsVoidOrders({ dateFrom: dateFrom || undefined, dateTo: dateTo || undefined }),
    staleTime: 60_000,
    retry: 1,
  });
  const rows = q.data?.rows ?? [];
  const totals = q.data?.totals;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <DateRange from={dateFrom} to={dateTo} onFrom={setDateFrom} onTo={setDateTo} />
        <p className="text-[11px] text-slate-500 self-center">
          A red <span className="text-red-400 font-medium">Shipped</span> flag = the order was voided AFTER it shipped.
        </p>
        <div className="flex-1" />
        <ResetButton onClick={resetFilters} />
        <ExportButton onClick={() => exportVoids(rows)} disabled={rows.length === 0} />
        <ExcelButton onClick={() => exportVoids(rows, exportXlsx)} disabled={rows.length === 0} />
        <RefreshButton onClick={() => q.refetch()} loading={q.isLoading} fetching={q.isFetching} />
      </div>

      {q.error && <ErrorBox title="Couldn't load voided orders" error={q.error} />}
      {q.isLoading ? <Spinner /> : !q.error && (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/50">
                  <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium">Order #</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Customer</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Order Date</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Req Date</th>
                  <th className="px-3 py-2.5 text-center text-slate-400 font-medium">Shipped</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.orderNo}-${i}`} className={`group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors ${r.shipped ? 'bg-red-950/20' : ''}`}>
                    <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 font-mono text-slate-300">{r.orderNo}</td>
                    <td className="px-3 py-2 text-white whitespace-nowrap max-w-[240px] truncate" title={`${r.code} ${r.name}`}>{r.name}</td>
                    <td className="px-3 py-2 text-slate-400 font-mono whitespace-nowrap">{r.orderDate}</td>
                    <td className="px-3 py-2 text-slate-400 font-mono whitespace-nowrap">{r.reqDate}</td>
                    <td className="px-3 py-2 text-center">
                      {r.shipped
                        ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/15 border border-red-500/40 text-red-300 font-medium"><Check size={11} /> shipped</span>
                        : <span className="text-slate-600">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtCur(r.total)}</td>
                  </tr>
                ))}
              </tbody>
              {totals && rows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-600/60 bg-slate-800/60 font-semibold">
                    <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 text-white">Total</td>
                    <td className="px-3 py-2 text-slate-400" colSpan={4}>{fmtNum(totals.rows)} void{totals.rows === 1 ? '' : 's'}</td>
                    <td className="px-3 py-2 text-right text-white tabular-nums">{fmtCur(totals.total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {rows.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">No voided orders in the selected range</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Short Shipped ────────────────────────────────────────────────

function exportShort(rows: OpsShortRow[], groupBy: string, exporter: typeof exportCsv = exportCsv) {
  const cols: CsvColumns<OpsShortRow> = [
    [groupBy,       (r) => r.label],
    ['Ordered Qty', (r) => r.orderedQty],
    ['Shipped Qty', (r) => r.shippedQty],
    ['Short Qty',   (r) => r.shortQty],
    ['Short %',     (r) => r.shortPct],
    ['Short Cost',  (r) => r.shortCost],
    ['Lines',       (r) => r.lines],
  ];
  exporter('ops-short-shipped', cols, rows);
}

function shortPctClass(pct: number): string {
  if (pct > 15) return 'text-red-400 font-medium';
  if (pct > 5) return 'text-amber-400 font-medium';
  return 'text-slate-300';
}

// Rows per source. Customer dimensions only exist for customer orders.
const GROUP_BYS: { value: string; label: string; orders?: boolean }[] = [
  { value: 'vendor',           label: 'Vendor' },
  { value: 'item',             label: 'Item' },
  { value: 'itemCategory',     label: 'Item Category' },
  { value: 'itemSubcategory',  label: 'Item Subcategory' },
  { value: 'area',             label: 'Cust Area',      orders: true },
  { value: 'customerCategory', label: 'Cust Category',  orders: true },
  { value: 'priceLevel',       label: 'Price Level',    orders: true },
  { value: 'salesman',         label: 'Salesman',       orders: true },
];

// Monthly matrix export: label + one column per month + total.
function exportShortMonthly(data: OpsShortResponse, groupBy: string, measure: 'cost' | 'qty', exporter: typeof exportCsv = exportCsv) {
  const months = data.months ?? [];
  const pick = (m?: OpsShortMonth) => (measure === 'cost' ? m?.shortCost : m?.shortQty) ?? 0;
  const cols: CsvColumns<OpsShortRow> = [
    [groupBy, (r) => r.label],
    ...months.map((ym, i): [string, (r: OpsShortRow) => number] => [ym, (r) => pick(r.byMonth?.[i])]),
    [measure === 'cost' ? 'Short $' : 'Short Qty', (r) => (measure === 'cost' ? r.shortCost : r.shortQty)],
  ];
  exporter(`ops-short-shipped-${data.source ?? 'purchases'}-monthly`, cols, [...data.rows, { ...data.totals, label: 'TOTAL' }]);
}

const pillCls = (on: boolean) => `px-2 py-1.5 text-[11px] rounded-md border transition-all ${
  on ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
     : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'}`;

function ShortView() {
  const { canSee } = useFeatureAccess();
  // Source / customer rows / monthly view: flag analytics.shortmatrix (agent 3.20.0+).
  const v2 = canSee('analytics.shortmatrix');
  const [dateFrom, setDateFrom] = useState(() => isoDaysAgo(90));
  const [dateTo, setDateTo] = useState(() => isoDaysAgo(0));
  const [groupBy, setGroupBy] = useState('vendor');
  const [sourcePref, setSource] = useState<'purchases' | 'orders'>('purchases');
  const [viewPref, setView] = useState<'totals' | 'monthly'>('totals');
  const [measure, setMeasure] = useState<'cost' | 'qty'>('cost');
  const [itemCategory, setItemCategory] = useState('');
  const [itemSubcategory, setItemSubcategory] = useState('');
  const { data: filters } = useQuery({
    queryKey: ['v2-analyticsFilters'],
    queryFn: () => api.getAnalyticsFilters(),
    staleTime: 5 * 60 * 1000,
  });
  const subcats = subcategoryOptions(filters, itemCategory);
  const source = v2 ? sourcePref : 'purchases';
  const view = v2 ? viewPref : 'totals';
  const groupBys = GROUP_BYS.filter(g => !g.orders || source === 'orders');
  const effGroupBy = groupBys.some(g => g.value === groupBy) ? groupBy : 'vendor';
  const groupLabel = GROUP_BYS.find(g => g.value === effGroupBy)?.label ?? effGroupBy;
  const resetFilters = () => {
    setDateFrom(isoDaysAgo(90)); setDateTo(isoDaysAgo(0)); setGroupBy('vendor');
    setSource('purchases'); setView('totals'); setMeasure('cost');
    setItemCategory(''); setItemSubcategory('');
  };

  const q = useQuery({
    queryKey: ['opsAudit', 'short', dateFrom, dateTo, effGroupBy, source, itemCategory, itemSubcategory],
    queryFn: () => api.getOpsShortShipped({
      dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, groupBy: effGroupBy,
      source: source === 'orders' ? 'orders' : undefined,
      itemCategory, itemSubcategory,
    }),
    staleTime: 60_000,
    retry: 1,
  });
  const data = q.data;
  const rows = data?.rows ?? [];
  const totals = data?.totals;
  const months = data?.months ?? [];
  const monthly = view === 'monthly' && months.length > 0;
  const valueLabel = source === 'orders' ? 'Short $' : 'Short Cost';
  const pickM = (m?: OpsShortMonth) => (measure === 'cost' ? m?.shortCost : m?.shortQty) ?? 0;
  const fmtM = (v: number) => (v === 0 ? '–' : measure === 'cost' ? fmtCur(v) : fmtNum(v));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <DateRange from={dateFrom} to={dateTo} onFrom={setDateFrom} onTo={setDateTo} />
        {(filters?.itemCategories?.length ?? 0) > 0 && (
          <FilterSelect label="Item Category" value={itemCategory} onChange={v => { setItemCategory(v); setItemSubcategory(''); }}
            options={(filters?.itemCategories ?? []).map(c => ({ value: c, label: c }))} />
        )}
        {subcats && (
          <FilterSelect label="Item Subcategory" value={itemSubcategory} onChange={setItemSubcategory}
            options={subcats.map(c => ({ value: c, label: c }))} />
        )}
        {v2 && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Source</span>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setSource('purchases')} className={pillCls(source === 'purchases')}
                title="Vendor purchase orders received short">Vendor POs</button>
              <button onClick={() => setSource('orders')} className={pillCls(source === 'orders')}
                title="Invoiced customer orders that shipped less than ordered">Customer orders</button>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Group By</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {groupBys.map(g => (
              <button key={g.value} onClick={() => setGroupBy(g.value)} className={pillCls(effGroupBy === g.value)}>
                {g.label}
              </button>
            ))}
          </div>
        </div>
        {v2 && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">View</span>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setView('totals')} className={pillCls(view === 'totals')}>Totals</button>
              <button onClick={() => setView('monthly')} className={pillCls(view === 'monthly')}>By month</button>
              {view === 'monthly' && (
                <>
                  <span className="w-px h-5 bg-slate-700/60 mx-0.5" />
                  <button onClick={() => setMeasure('cost')} className={pillCls(measure === 'cost')}>$</button>
                  <button onClick={() => setMeasure('qty')} className={pillCls(measure === 'qty')}>Qty</button>
                </>
              )}
            </div>
          </div>
        )}
        <div className="flex-1" />
        <ResetButton onClick={resetFilters} />
        <ExportButton
          onClick={() => data && (monthly ? exportShortMonthly(data, groupLabel, measure) : exportShort(rows, groupLabel))}
          disabled={rows.length === 0} />
        <ExcelButton
          onClick={() => data && (monthly ? exportShortMonthly(data, groupLabel, measure, exportXlsx) : exportShort(rows, groupLabel, exportXlsx))}
          disabled={rows.length === 0} />
        <RefreshButton onClick={() => q.refetch()} loading={q.isLoading} fetching={q.isFetching} />
      </div>

      {q.error && <ErrorBox title="Couldn't load short-shipments" error={q.error} />}
      {q.isLoading ? <Spinner /> : !q.error && (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
          <div className="overflow-x-auto">
            {monthly ? (
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700/50 bg-slate-800/50">
                    <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[160px]">{groupLabel}</th>
                    {months.map(ym => <th key={ym} className="px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap">{ym}</th>)}
                    <th className="px-3 py-2.5 text-right text-slate-300 font-semibold whitespace-nowrap">{measure === 'cost' ? valueLabel : 'Short Qty'}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.label}-${i}`} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                      <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-white whitespace-nowrap max-w-[260px] truncate" title={r.label}>{r.label}</td>
                      {months.map((ym, mi) => (
                        <td key={ym} className="px-3 py-2 text-right text-slate-300 tabular-nums whitespace-nowrap">{fmtM(pickM(r.byMonth?.[mi]))}</td>
                      ))}
                      <td className="px-3 py-2 text-right text-white tabular-nums whitespace-nowrap">{measure === 'cost' ? fmtCur(r.shortCost) : fmtNum(r.shortQty)}</td>
                    </tr>
                  ))}
                </tbody>
                {totals && rows.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-600/60 bg-slate-800/60 font-semibold">
                      <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 text-white">Total</td>
                      {months.map((ym, mi) => (
                        <td key={ym} className="px-3 py-2 text-right text-white tabular-nums whitespace-nowrap">{fmtM(pickM(totals.byMonth?.[mi]))}</td>
                      ))}
                      <td className="px-3 py-2 text-right text-white tabular-nums whitespace-nowrap">{measure === 'cost' ? fmtCur(totals.shortCost) : fmtNum(totals.shortQty)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/50">
                  <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[160px]">
                    {groupLabel}
                  </th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Ordered</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Shipped</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Short Qty</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Short %</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">{valueLabel}</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Lines</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.label}-${i}`} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                    <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-white whitespace-nowrap max-w-[260px] truncate" title={r.label}>{r.label}</td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtNum(r.orderedQty)}</td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtNum(r.shippedQty)}</td>
                    <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtNum(r.shortQty)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${shortPctClass(r.shortPct)}`}>{r.shortPct.toFixed(1)}%</td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtCur(r.shortCost)}</td>
                    <td className="px-3 py-2 text-right text-slate-500 tabular-nums">{fmtNum(r.lines)}</td>
                  </tr>
                ))}
              </tbody>
              {totals && rows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-600/60 bg-slate-800/60 font-semibold">
                    <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 text-white">Total</td>
                    <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtNum(totals.orderedQty)}</td>
                    <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtNum(totals.shippedQty)}</td>
                    <td className="px-3 py-2 text-right text-white tabular-nums">{fmtNum(totals.shortQty)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${shortPctClass(totals.shortPct)}`}>{totals.shortPct.toFixed(1)}%</td>
                    <td className="px-3 py-2 text-right text-white tabular-nums">{fmtCur(totals.shortCost)}</td>
                    <td className="px-3 py-2 text-right text-slate-400 tabular-nums">{fmtNum(totals.lines)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
            )}
          </div>
          {rows.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">No short-shipments in the selected range</div>
          )}
          {v2 && view === 'monthly' && data && data.months === undefined && (
            <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-amber-400">Monthly columns need agent 3.20.0+</div>
          )}
          {source === 'orders' && data?.source === 'orders' && (
            <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-slate-500">
              Invoiced customer orders by delivery date · valued at the order price · history only as deep as the order cache keeps it
            </div>
          )}
        </div>
      )}
    </div>
  );
}
